import { getAllSceneNodes } from "@/document/model/document";
import { subdivideEdges } from "@/document/operations/vectorSubdivide";
import { documentStore, selectionStore, uiStore, viewStore } from "@/editor/store";

/** Rebuilds the live topology preview from the current subdivide session. */
export function refreshSubdividePreview(connect = uiStore.subdivideSession.getValue()?.connect ?? false): void {
  const session = uiStore.subdivideSession.getValue();
  if (!session) {
    viewStore.setVectorTopologyPreview(undefined);
    return;
  }

  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === session.nodeId);
  if (node?.type !== "vectorNetwork") {
    cancelSubdivideSession(false);
    return;
  }

  const result = subdivideEdges(node.network, session.edgeIds, {
    segments: session.segments,
    connect,
  });
  viewStore.setVectorTopologyPreview({
    nodeId: session.nodeId,
    network: result.network,
    changedEdgeIds: result.changedEdgeIds,
    changedVertexIds: result.newVertexIds,
    newVertexIds: result.newVertexIds,
  });
}

export function setSubdivideConnect(connect: boolean): void {
  const session = uiStore.subdivideSession.getValue();
  if (!session || session.connect === connect) {
    return;
  }
  uiStore.patchSubdivideSession({ connect });
  refreshSubdividePreview(connect);
}

export function cancelSubdivideSession(restoreTool = true): boolean {
  const session = uiStore.subdivideSession.getValue();
  const activeTool = selectionStore.tool.getValue().activeTool;
  if (!session && activeTool !== "subdivide") {
    return false;
  }

  uiStore.closeSubdivideSession();
  viewStore.setVectorTopologyPreview(undefined);
  if (restoreTool) {
    selectionStore.setActiveTool(session?.previousTool ?? "select");
  }
  return true;
}

/** Returns true when the live edge selection still matches the subdivide session. */
export function subdivideSessionMatchesSelection(edgeIds: string[], nodeId: string | undefined): boolean {
  const session = uiStore.subdivideSession.getValue();
  if (!session) {
    return true;
  }
  if (session.nodeId !== nodeId || session.edgeIds.length !== edgeIds.length) {
    return false;
  }
  const selected = new Set(edgeIds);
  return session.edgeIds.every(edgeId => selected.has(edgeId));
}
